'use client';

import { AnimatePresence, motion } from 'motion/react';
import dynamic from 'next/dynamic';
import { ReactNode, useEffect, useState } from 'react';
import { z } from 'zod';
import { useServerAction } from 'zsa-react';

import { Link } from '@natu/next-link';

import { CoreWebVitalsForm } from './CoreWebVitalsForm';
import { coreWebVitalsFormSchema } from './coreWebVitalsFormSchema';
import { getLighthouseScore } from './getLighthouseScore';
import { LighthouseScoreAnimatedGauge } from './LighthouseScoreAnimatedGauge';
import { LighthouseScorePerformanceLegend } from './LighthouseScorePerformanceLegend';

const Button = dynamic(() => import('@natu/ui/Button').then(mod => mod.Button));

const DynamicIcon = dynamic(() => import('@natu/ui/DynamicIcon').then(mod => mod.DynamicIcon));

interface CoreWebVitalsContentProps {
  heading?: string;
  subheading?: string;
  notBadScore?: string;
  buttonLabel?: string;
  resolvedUrl?: {
    target?: '_blank' | undefined;
    rel?: 'noreferrer' | undefined;
    href: string;
  };
  badScore?: string;
  greatScore?: string;
  testLengthInfo?: string;
  errorMessage?: string;
  carousel?: ReactNode;
}

export const CoreWebVitalsContent = ({
  heading,
  subheading,
  notBadScore,
  buttonLabel,
  resolvedUrl,
  badScore,
  greatScore,
  errorMessage,
  testLengthInfo,
  carousel,
}: CoreWebVitalsContentProps) => {
  const performanceStates = [
    {
      scoreRange: [0, 49],
      label: 'Bad',
      fill: '#F04438',
      explanation: badScore,
    },
    {
      scoreRange: [50, 89],
      label: 'Not Bad',
      fill: '#F79009',
      explanation: notBadScore,
    },
    {
      scoreRange: [90, 100],
      label: 'Great',
      fill: '#18B068',
      explanation: greatScore,
    },
  ];

  const [score, setScore] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showCarousel, setShowCarousel] = useState(false);

  const handlePerformanceScoreChange = (newScore?: number) => {
    if (!newScore) return;
    setScore(newScore);
  };

  useEffect(() => {
    if (score < 50) setActiveIndex(0);
    else if (score < 90) setActiveIndex(1);
    else setActiveIndex(2);
  }, [score]);

  const { label, fill, explanation } = performanceStates[activeIndex];

  const { execute, isPending, isSuccess, isError } = useServerAction(getLighthouseScore, {
    onSuccess: ({ data }) => {
      handlePerformanceScoreChange(Math.trunc(+data));
    },
  });

  const onSubmit = async (values: z.infer<typeof coreWebVitalsFormSchema>) => {
    await execute(values);
  };

  useEffect(() => {
    if (isPending && !showCarousel) {
      setShowCarousel(true);
    }
  }, [isPending, showCarousel]);

  return (
    <div className="flex flex-col gap-y-12 lg:flex-row lg:items-center lg:gap-x-24 lg:py-10">
      <div className="max-w-[596px]">
        {heading && (
          <h2 className="text-display-sm-28 xl:text-display-md-34 mb-6 text-center font-semibold lg:mb-8 lg:text-left">
            {heading}
          </h2>
        )}
        {subheading && <p className="mb-4 text-center text-gray-600 lg:text-left">{subheading}</p>}
        <CoreWebVitalsForm
          errorMessage={errorMessage}
          isPending={isPending}
          testLengthInfo={testLengthInfo}
          isError={isError}
          onSubmit={onSubmit}
        />
        <AnimatePresence mode="wait">
          {showCarousel && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <div className="pt-4">{carousel}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="rounded-xl border border-gray-200 px-6 py-8 shadow-md lg:w-96 lg:shrink-0 xl:w-[474px]">
        <p className="mb-4 text-center text-lg font-bold text-gray-500">Performance Score</p>
        <div className="mb-6">
          <LighthouseScoreAnimatedGauge
            percentage={score}
            color={fill}
            label={label}
            isSuccess={isSuccess}
            isPending={isPending}
            isError={isError}
          />
        </div>
        <div className="flex flex-col gap-y-4">
          <div className="flex flex-col gap-y-1 lg:flex-row lg:flex-wrap lg:justify-center lg:gap-x-5">
            {performanceStates.map((item, index) => (
              <LighthouseScorePerformanceLegend
                key={`lighthouse-legend-${item.label}`}
                color={item.fill}
                label={`${performanceStates[index].scoreRange[0]}-${performanceStates[index].scoreRange[1]} - ${item.label}`}
              />
            ))}
          </div>
          <AnimatePresence mode="wait">
            {isSuccess && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                className="overflow-hidden"
              >
                <p className="mb-8 text-center text-base text-gray-700 xl:text-lg">{explanation}</p>
                {buttonLabel && resolvedUrl && (
                  <div className="text-center">
                    <Button
                      asChild
                      variant="outline"
                      className="text-[0.813rem] font-semibold uppercase leading-[1.25rem]"
                    >
                      <Link {...resolvedUrl}>
                        {buttonLabel}
                        <DynamicIcon className="ml-3" icon="arrowRight" />
                      </Link>
                    </Button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
